/**
 * CORE-629 — schedules "mark this thread read" while it stays open.
 *
 * Both chat bindings (`hooks/useChatThreadSource` on web,
 * `screens/messages/useChatThread` on native) used to call `markRead()` exactly
 * once, right after `connect()`. Every message that landed afterwards — above
 * all Agapella's reply to what the user had just sent — was rendered as read but
 * left unread on the server, so the badge went on counting messages already on
 * screen and no amount of re-reading or hard-refreshing cleared it.
 *
 * Kept as a plain object rather than a hook so it is testable without a React
 * renderer, and so both bindings share one definition of the rules:
 *   - debounce, because a streaming AI reply is one MessageNew followed by a
 *     burst of MessageUpdated (one MarkRead per token would hammer the hub);
 *   - never claim a read while the surface is hidden — a message arriving in a
 *     background tab has not been read by anyone.
 */
export interface ReadPointer {
  /** Note that the thread changed; marks read once the burst settles. */
  schedule(): void;
  /** Drop any pending mark (thread unmounted / channel switched). */
  cancel(): void;
}

export interface ReadPointerOptions {
  /** Advance the server read pointer. Called at most once per settled burst. */
  markRead: () => void;
  /** Whether the thread is actually on screen. Defaults to always visible. */
  isVisible?: () => boolean;
  /** Quiet period before marking read. */
  delayMs?: number;
}

export const READ_POINTER_DEBOUNCE_MS = 600;

export function createReadPointer({
  markRead,
  isVisible = () => true,
  delayMs = READ_POINTER_DEBOUNCE_MS,
}: ReadPointerOptions): ReadPointer {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  const cancel = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    if (cancelled || !isVisible()) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      // Re-check: the tab may have been hidden, or the thread torn down, during
      // the quiet period.
      if (!cancelled && isVisible()) markRead();
    }, delayMs);
  };

  return { schedule, cancel };
}
